'use client';

import { useQuery } from '@tanstack/react-query';

import { getQueryClient } from 'app/get-query-client';
import { useAuthStore } from 'lib/stores/authStore';
import type { ServerError } from 'models';
import type { BankAccountVo, PaymentVendorVerificationStatus } from 'models/banks';
import type { SelectOptions } from 'models/constants';
import type { PaymentVendorMetadata } from 'models/reqres';
import {
    ensurePaymentsMetadataResponse,
    getVendorMetadata,
    invalidatePaymentsMetadata,
} from 'queries/paymentsMetadata';
import { withdrawalLimitsQueryOptions } from 'queries/withdrawalLimits';
import { getBankAccounts, getPlaidAccounts, removeBankAccount, removePlaidAccount } from 'transport/domains/banking';
import { noop } from 'utils/noop';

// Linked bank accounts come from two independent endpoints — Aeropay
// (getBankAccounts) and Plaid (getPlaidAccounts). React Query owns the fetch;
// this module also holds the pure BankAccount data shape and the join logic
// that used to live in BanksStore.resolveCreateAccount/updateAccounts.
export type BankLinkingProvider = 'aeropay' | 'plaid';

export interface BankAccountData {
    id: string;
    name: string;
    provider: BankLinkingProvider;
    mask: string;
    paymentVendorVerificationStatus: PaymentVendorVerificationStatus;
    failureReason: string;
    institutionName: string;
}

export function bankAccountFromVo(vo: BankAccountVo, provider: BankLinkingProvider): BankAccountData {
    return {
        id: vo.bank_id,
        name: vo.name,
        mask: vo.mask,
        paymentVendorVerificationStatus: vo.payment_vendor_verification_status ?? 'unknown',
        failureReason: vo.failure_reason ?? '',
        institutionName: vo.institution_name ?? '',
        provider,
    };
}

// Bank IDs for "layer" (instant-funding-eligible) Plaid accounts are prefixed
// with 'L-' for linking purposes; transfers reference the unprefixed ID.
// Ported verbatim from BanksStore.resolveCreateAccount.
export function stripBankIdLayerPrefix(bankId: string): string {
    return bankId.startsWith('L-') ? bankId.substring(2) : bankId;
}

// Merges the two providers' account lists into one map keyed by bank_id — the
// same join BanksStore.updateAccounts performed (Aeropay first, Plaid second;
// a bank_id present in both is left as the Plaid entry, matching load()'s call
// order). Unlinked Aeropay accounts are filtered out, matching load()'s filter.
export function mergeBankAccountsById(
    aeropayAccounts: BankAccountVo[],
    plaidAccounts: BankAccountVo[],
): Map<string, BankAccountData> {
    const accountsById = new Map<string, BankAccountData>();
    aeropayAccounts
        .filter((vo) => !vo.unlinked)
        .forEach((vo) => accountsById.set(vo.bank_id, bankAccountFromVo(vo, 'aeropay')));
    plaidAccounts.forEach((vo) => accountsById.set(vo.bank_id, bankAccountFromVo(vo, 'plaid')));
    return accountsById;
}

// Keyed the way Transfer.bankId references accounts (layer prefix stripped) —
// the join BanksStore.transferAccounts provided for Transfer.account.
export function mapAccountsByTransferBankId(accountsById: Map<string, BankAccountData>): Map<string, BankAccountData> {
    const transferAccountsById = new Map<string, BankAccountData>();
    accountsById.forEach((account, bankId) => {
        transferAccountsById.set(stripBankIdLayerPrefix(bankId), account);
    });
    return transferAccountsById;
}

// Resolve the ACH cap/limit from this account's own vendor, not the user's
// first-enabled ACH vendor: once Aeropay and Plaid can both be enabled, an
// Aeropay account must show Aeropay's cap and a Plaid account Plaid's cap.
// Ported verbatim from BankAccount.vendorAchMetadata.
export function getBankAccountAchVendorMetadata(
    account: BankAccountData,
    vendors: PaymentVendorMetadata[],
): PaymentVendorMetadata | undefined {
    return getVendorMetadata(vendors, account.provider, 'ach');
}

export function getBankAccountLimitInCents(
    account: BankAccountData,
    vendors: PaymentVendorMetadata[],
): number | undefined {
    return getBankAccountAchVendorMetadata(account, vendors)?.max_deposit_amount_cents;
}

export function getBankAccountLimitInDollars(
    account: BankAccountData,
    vendors: PaymentVendorMetadata[],
): number | undefined {
    const cents = getBankAccountLimitInCents(account, vendors);
    return cents === undefined ? undefined : cents / 100;
}

export function getBankAccountDepositLimitReason(
    account: BankAccountData,
    vendors: PaymentVendorMetadata[],
): string | undefined {
    return getBankAccountAchVendorMetadata(account, vendors)?.max_deposit_reason || undefined;
}

export function isBankAccountBlocked(account: BankAccountData): boolean {
    return account.paymentVendorVerificationStatus === 'blocked';
}

export function getBankAccountFailureMessage(account: BankAccountData): string | undefined {
    if (!isBankAccountBlocked(account)) {
        return undefined;
    }
    if (!account.failureReason) {
        return 'This account is blocked. Please contact support for more information.';
    }
    return account.failureReason === 'duplicate bank account'
        ? 'This bank account is already linked to a different Kalshi account. Please connect a different bank account. If you believe this action was unauthorized, please contact support.'
        : account.failureReason;
}

export const bankAccountsKeys = {
    all: ['bankAccounts'] as const,
    aeropay: (userId: string | null) => ['bankAccounts', 'aeropay', userId] as const,
    plaid: (userId: string | null) => ['bankAccounts', 'plaid', userId] as const,
};

// Shared settled-guard: a forced disconnect makes handleErrorCallback swallow
// the error (no onErr), so onFinally rejects if neither onSuc nor onErr fired,
// keeping the RQ promise from hanging forever.
function fetchBankAccountsFromProvider(
    fetchAccounts: (
        userId: string,
        onSuc: (accounts?: BankAccountVo[]) => void,
        onErr: (err: ServerError) => void,
        onFinally: () => void,
    ) => void,
    userId: string,
    providerLabel: string,
): Promise<BankAccountVo[]> {
    return new Promise((resolve, reject) => {
        let settled = false;
        fetchAccounts(
            userId,
            (accounts) => {
                settled = true;
                resolve(accounts ?? []);
            },
            (err) => {
                settled = true;
                reject(err);
            },
            () => {
                if (!settled) {
                    settled = true;
                    reject(new Error(`${providerLabel} bank accounts request did not settle`));
                }
            },
        );
    });
}

export function fetchAeropayBankAccounts(userId: string): Promise<BankAccountVo[]> {
    return fetchBankAccountsFromProvider(getBankAccounts, userId, 'aeropay');
}

export function fetchPlaidBankAccounts(userId: string): Promise<BankAccountVo[]> {
    return fetchBankAccountsFromProvider(getPlaidAccounts, userId, 'plaid');
}

async function loadPlaidBankAccounts(userId: string): Promise<BankAccountVo[]> {
    return (await ensurePaymentsMetadataResponse(userId)).plaid_bank_accounts;
}

async function loadAeropayBankAccounts(userId: string): Promise<BankAccountVo[]> {
    return (await ensurePaymentsMetadataResponse(userId)).linked_aeropay_accounts;
}

export function aeropayBankAccountsQueryOptions(userId: string) {
    return {
        queryKey: bankAccountsKeys.aeropay(userId),
        queryFn: () => loadAeropayBankAccounts(userId),
    };
}

export function plaidBankAccountsQueryOptions(userId: string) {
    return {
        queryKey: bankAccountsKeys.plaid(userId),
        queryFn: () => loadPlaidBankAccounts(userId),
    };
}

// True when the bank-account queries are actually allowed to run. Shared by
// useBankAccountsQueryForProvider (as the `enabled` gate) and useBankAccountsData
// (to know the disabled case counts as "loaded", matching BanksStore.load()'s
// guard paths, which explicitly marked accounts loaded for logged-out/no-access
// users instead of leaving the UI stuck on a spinner forever).
function useIsBankAccountsQueryEnabled(): boolean {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);
    // Select the boolean field directly, not `state.derived` (a stable object whose
    // hasTransfersAccess() method reads fresh state via get() at call time) — selecting
    // the object means zustand never sees a changed return value, so the component
    // won't re-render when transfer access is revoked/granted for the same userId
    // (e.g. switching into a no-transfer-scope sub-user), leaving isEnabled stale.
    const hasTransfersAccess = useAuthStore((state) => state.hasTransferAccess);
    return !!userId && isLoggedInMember && hasTransfersAccess;
}

function useBankAccountsQueryForProvider(
    queryOptionsForUser: (userId: string) => { queryKey: readonly unknown[]; queryFn: () => Promise<BankAccountVo[]> },
) {
    const userId = useAuthStore((s) => s.userId);
    const enabled = useIsBankAccountsQueryEnabled();

    return useQuery({
        ...queryOptionsForUser(userId ?? ''),
        enabled,
    });
}

export function useAeropayBankAccountsQuery() {
    return useBankAccountsQueryForProvider(aeropayBankAccountsQueryOptions);
}

export function usePlaidBankAccountsQuery() {
    return useBankAccountsQueryForProvider(plaidBankAccountsQueryOptions);
}

export interface BankAccountsData {
    accountsById: Map<string, BankAccountData>;
    transferAccountsById: Map<string, BankAccountData>;
    linkedAccountsArray: BankAccountData[];
    numberOfActiveAccounts: number;
    isLoadedAccounts: boolean;
}

// A disabled query never fetches, so isFetched/isError would otherwise stay
// false forever and consumers gating on "loaded" (e.g. the sign-up banking
// flow, deposit method picker) would spin indefinitely.
export function getIsLoadedAccounts(params: {
    isEnabled: boolean;
    aeropayIsFetched: boolean;
    aeropayIsError: boolean;
    plaidIsFetched: boolean;
    plaidIsError: boolean;
}): boolean {
    if (!params.isEnabled) return true;
    // Mirrors BanksStore.load()'s "loaded once both calls complete, success or
    // failure" semantics so the UI never hangs on a spinner if one call fails.
    return (params.aeropayIsFetched || params.aeropayIsError) && (params.plaidIsFetched || params.plaidIsError);
}

// Combines both providers into the merged views BanksStore used to maintain
// (linkedAccounts / transferAccounts / linkedAccountsArray / numberOfActiveAccounts).
export function useBankAccountsData(): BankAccountsData {
    const aeropay = useAeropayBankAccountsQuery();
    const plaid = usePlaidBankAccountsQuery();
    const isEnabled = useIsBankAccountsQueryEnabled();

    // React Query can still return cached data under the same ['bankAccounts', ...,
    // userId] keys from an earlier, higher-access session (e.g. full access -> logged
    // back in as a sub-user without transfer scope on the same account). Disabled means
    // "not allowed to see this", not just "don't fetch" — never surface stale cached data.
    const accountsById = mergeBankAccountsById(
        isEnabled ? (aeropay.data ?? []) : [],
        isEnabled ? (plaid.data ?? []) : [],
    );
    const linkedAccountsArray = Array.from(accountsById.values());

    return {
        accountsById,
        transferAccountsById: mapAccountsByTransferBankId(accountsById),
        linkedAccountsArray,
        numberOfActiveAccounts: linkedAccountsArray.length,
        isLoadedAccounts: getIsLoadedAccounts({
            isEnabled,
            aeropayIsFetched: aeropay.isFetched,
            aeropayIsError: aeropay.isError,
            plaidIsFetched: plaid.isFetched,
            plaidIsError: plaid.isError,
        }),
    };
}

export function getAccountOptions(accounts: BankAccountData[]): SelectOptions[] {
    return accounts.map((account) => {
        const accountName = account.institutionName || account.name;
        const isBlocked = isBankAccountBlocked(account);
        return {
            label: isBlocked ? `${accountName} (Blocked)` : `${accountName} (•••• ${account.mask})`,
            value: account.id,
            disabled: isBlocked,
            tooltip: getBankAccountFailureMessage(account),
        };
    });
}

// Mutation paths keep a writer-backed read for immediate consistency, seed the
// provider cache, then invalidate the aggregate display snapshot.
async function refetchBankAccountsForProvider(
    userId: string,
    provider: BankLinkingProvider,
    fetchAccounts: (userId: string) => Promise<BankAccountVo[]>,
): Promise<BankAccountVo[]> {
    try {
        const accounts = await fetchAccounts(userId);
        const queryKey = provider === 'aeropay' ? bankAccountsKeys.aeropay(userId) : bankAccountsKeys.plaid(userId);
        getQueryClient().setQueryData(queryKey, accounts);
        return accounts;
    } finally {
        invalidatePaymentsMetadata(userId);
    }
}

export function refetchAeropayBankAccounts(userId: string): Promise<BankAccountVo[]> {
    return refetchBankAccountsForProvider(userId, 'aeropay', fetchAeropayBankAccounts);
}

export function refetchPlaidBankAccounts(userId: string): Promise<BankAccountVo[]> {
    return refetchBankAccountsForProvider(userId, 'plaid', fetchPlaidBankAccounts);
}

// Optimistically merges a freshly linked account into the provider's cache so
// useBankAccountsData() reflects it immediately — matching the old
// BanksStore.updateAccounts, which synchronously wrote into `linkedAccounts`
// before the success callback fired. Without this, a caller that reopens the
// deposit flow right after onLinkAccountSuccess (before refreshAfterAccountLinked's
// network refetch resolves) would still see the pre-link, empty account list.
export function seedLinkedBankAccountInCache(userId: string, vo: BankAccountVo, provider: BankLinkingProvider): void {
    const queryKey = provider === 'aeropay' ? bankAccountsKeys.aeropay(userId) : bankAccountsKeys.plaid(userId);
    getQueryClient().setQueryData<BankAccountVo[]>(queryKey, (existing = []) => [
        ...existing.filter((account) => account.bank_id !== vo.bank_id),
        vo,
    ]);
    invalidatePaymentsMetadata(userId);
}

// After linking/updating an account, refresh both the account list for the
// linked provider and withdrawal limits (whose availability depends on the
// newly linked account) — matching BanksStore.updateAccounts.
export async function refreshAfterAccountLinked(userId: string, provider: BankLinkingProvider): Promise<void> {
    await Promise.all([
        provider === 'aeropay' ? refetchAeropayBankAccounts(userId) : refetchPlaidBankAccounts(userId),
        getQueryClient()
            .fetchQuery({ ...withdrawalLimitsQueryOptions(userId, 'event_contract'), staleTime: 0 })
            .catch(noop),
    ]);
}

export function removeAeropayBankAccount(userId: string, bankId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        removeBankAccount(
            { user_id: userId, bank_id: bankId },
            () => resolve(),
            (err) => reject(err),
        );
    });
}

export function removePlaidBankAccount(userId: string, bankId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        removePlaidAccount(
            userId,
            bankId,
            () => {
                invalidatePaymentsMetadata(userId);
                resolve();
            },
            (err) => reject(err),
        );
    });
}
